import { Weight } from '@rematter/frontend-lib-api-client';
import { enumComplianceOptions } from '@rematter/modules/trade/types/scaleTickets';
import { atom } from 'jotai';

export type WeightFieldType = 'gross' | 'tare';

// 🔘🔘🔘 PLAIN ATOMS
export const $lastFocusedRowId = atom<string | null>(null);
export const $lastFocusedWeightFieldType = atom<WeightFieldType | null>(null);
export const $autoFocusRowId = atom<string | null>(null);

export type MaterialSortingRules = {
  salesOrderItemIds: Set<string>;
  requestedItemIds: Set<string>;
};

export const $materialSortingRules = atom<MaterialSortingRules>({
  salesOrderItemIds: new Set<string>(),
  requestedItemIds: new Set<string>(),
});

// 🔘🔘🔘 DERIVED ATOMS
export const $headerRect = atom({
  width: 0,
  height: 0,
  left: 0,
  top: 0,
  bottom: 0,
  right: 0,
});

// 🔘🔘🔘 WRITE-ONLY ATOMS
export const $setAutoFocusRowId = atom(
  null,
  (_get, set, rowId: string | null) => {
    set($autoFocusRowId, rowId);
  },
);

export const $clearAutoFocusRowId = atom(null, (_get, set) => {
  set($autoFocusRowId, null);
});

export const $setHeaderRect = atom(
  null,
  (
    _get,
    set,
    rect: {
      width: number;
      height: number;
      left: number;
      top: number;
      bottom: number;
      right: number;
    },
  ) => {
    set($headerRect, rect);
  },
);

// 🔘🔘🔘 CATALYTIC MODAL STATE
export type CatalyticModalMode = 'add' | 'change' | 'edit';

export type CatalyticModalState = {
  isOpen: boolean;
  mode: CatalyticModalMode;
  selectedItemId?: string | null;
  lineItemId?: string | null;
};

export const $catalyticModalState = atom<CatalyticModalState>({
  isOpen: false,
  mode: 'add',
});

export const $openCatalyticModal = atom(
  null,
  (
    _get,
    set,
    params: {
      mode: CatalyticModalMode;
      selectedItemId?: string | null;
      lineItemId?: string | null;
    },
  ) => {
    const { mode, selectedItemId = null, lineItemId = null } = params;
    set($catalyticModalState, {
      isOpen: true,
      mode,
      selectedItemId,
      lineItemId,
    });
  },
);

export const $closeCatalyticModal = atom(null, (_get, set) => {
  set($catalyticModalState, { isOpen: false, mode: 'add' });
});

// 🔘🔘🔘 CATALYTIC DETAILS DIALOG STATE
export type CatalyticDetailsState = {
  isOpen: boolean;
  lineItemId: string | null;
  itemId?: string | null;
  serialNumber?: string | null;
  oemCode?: string | null;
  compliance: string;
};

export const $catalyticDetailsState = atom<CatalyticDetailsState>({
  isOpen: false,
  lineItemId: null,
  compliance: enumComplianceOptions.documented,
});

export const $openCatalyticDetails = atom(
  null,
  (
    _get,
    set,
    params: {
      lineItemId: string;
      itemId?: string | null;
      serialNumber?: string | null;
      oemCode?: string | null;
      compliance?: string;
    },
  ) => {
    set($catalyticDetailsState, {
      isOpen: true,
      lineItemId: params.lineItemId,
      itemId: params.itemId,
      serialNumber: params.serialNumber,
      oemCode: params.oemCode,
      compliance: params.compliance ?? enumComplianceOptions.documented,
    });
  },
);

export const $closeCatalyticDetails = atom(null, (_get, set) => {
  set($catalyticDetailsState, {
    isOpen: false,
    lineItemId: null,
    compliance: enumComplianceOptions.documented,
  });
});

// 🔘🔘🔘 VEHICLE MODAL STATE
export type VehicleModalState = {
  isOpen: boolean;
  selectedItemId?: string | null;
  lineItemId?: string | null;
};

export const $vehicleModalState = atom<VehicleModalState>({
  isOpen: false,
});

export const $openVehicleModal = atom(
  null,
  (
    _get,
    set,
    params: {
      selectedItemId?: string | null;
      lineItemId?: string | null;
    },
  ) => {
    set($vehicleModalState, {
      isOpen: true,
      selectedItemId: params.selectedItemId ?? null,
      lineItemId: params.lineItemId ?? null,
    });
  },
);

export const $closeVehicleModal = atom(null, (_get, set) => {
  set($vehicleModalState, { isOpen: false });
});

// 🔘🔘🔘 VEHICLE DETAILS DIALOG STATE
export type VehicleDetailsState = {
  isOpen: boolean;
  lineItemId: string | null;
  itemId?: string | null;
  vin?: string | null;
  make?: string | null;
  model?: string | null;
  year?: number | null;
};

export const $vehicleDetailsState = atom<VehicleDetailsState>({
  isOpen: false,
  lineItemId: null,
});

export const $openVehicleDetails = atom(
  null,
  (
    _get,
    set,
    params: {
      lineItemId: string;
      itemId?: string | null;
      vin?: string | null;
      make?: string | null;
      model?: string | null;
      year?: number | null;
    },
  ) => {
    set($vehicleDetailsState, {
      isOpen: true,
      lineItemId: params.lineItemId,
      itemId: params.itemId,
      vin: params.vin,
      make: params.make,
      model: params.model,
      year: params.year,
    });
  },
);

export const $closeVehicleDetails = atom(null, (_get, set) => {
  set($vehicleDetailsState, {
    isOpen: false,
    lineItemId: null,
  });
});

// 🔘🔘🔘 MIXED CHILD ADD STATE
export type MixedChildAddState = {
  isOpen: boolean;
  parentLineItemId: string | null;
};

export const $mixedChildAddState = atom<MixedChildAddState>({
  isOpen: false,
  parentLineItemId: null,
});

export const $openMixedChildAdd = atom(
  null,
  (_get, set, parentLineItemId: string) => {
    set($mixedChildAddState, { isOpen: true, parentLineItemId });
  },
);

export const $closeMixedChildAdd = atom(null, (_get, set) => {
  set($mixedChildAddState, { isOpen: false, parentLineItemId: null });
});

// 🔘🔘🔘 MIXED DETAILS DIALOG STATE
export type MaterialBreakdown = {
  itemId: string;
  itemName: string;
  itemGroup?: string;
  mnemonic?: string | null;
  percentage: number;
};

export type MixedDetailsState = {
  isOpen: boolean;
  lineItemId: string | null;
  weights: Record<string, Weight | null> | null;
  notes: string;
  breakdown: MaterialBreakdown[];
};

export const $mixedDetailsState = atom<MixedDetailsState>({
  isOpen: false,
  lineItemId: null,
  weights: null,
  notes: '',
  breakdown: [],
});

export const $openMixedDetails = atom(
  null,
  (
    _get,
    set,
    params: {
      lineItemId: string;
      weights: Record<string, Weight | null>;
      notes: string;
      breakdown: MaterialBreakdown[];
    },
  ) => {
    set($mixedDetailsState, {
      isOpen: true,
      ...params,
    });
  },
);

export const $closeMixedDetails = atom(null, (_get, set) => {
  set($mixedDetailsState, {
    isOpen: false,
    lineItemId: null,
    weights: null,
    notes: '',
    breakdown: [],
  });
});

// 🔘🔘🔘 RAW MATERIAL DETAILS DIALOG STATE
export type RawMaterialDetailsState = {
  isOpen: boolean;
  lineItemId: string | null;
  grossWeight: number;
  tareWeight: number;
  notes: string;
};

export const $rawMaterialDetailsState = atom<RawMaterialDetailsState>({
  isOpen: false,
  lineItemId: null,
  grossWeight: 0,
  tareWeight: 0,
  notes: '',
});

export const $openRawMaterialDetails = atom(
  null,
  (
    _get,
    set,
    params: {
      lineItemId: string;
      grossWeight: number;
      tareWeight: number;
      notes: string;
    },
  ) => {
    set($rawMaterialDetailsState, {
      isOpen: true,
      ...params,
    });
  },
);

export const $closeRawMaterialDetails = atom(null, (_get, set) => {
  set($rawMaterialDetailsState, {
    isOpen: false,
    lineItemId: null,
    grossWeight: 0,
    tareWeight: 0,
    notes: '',
  });
});

// 🔘🔘🔘 FREIGHT DETAILS DIALOG STATE
export type FreightDetailsState = {
  isOpen: boolean;
  lineItemId: string | null;
  notes?: string | null;
};

export const $freightDetailsState = atom<FreightDetailsState>({
  isOpen: false,
  lineItemId: null,
});

export const $openFreightDetails = atom(
  null,
  (
    _get,
    set,
    params: Omit<FreightDetailsState, 'isOpen'> & { lineItemId: string },
  ) => {
    set($freightDetailsState, {
      isOpen: true,
      ...params,
    });
  },
);

export const $closeFreightDetails = atom(null, (_get, set) => {
  set($freightDetailsState, {
    isOpen: false,
    lineItemId: null,
  });
});
